"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const fmko_ts_widgets_1 = require("fmko-ts-widgets");
const DeleteRowSchemaColumnConfig_1 = tslib_1.__importDefault(require("../schema-column-config/DeleteRowSchemaColumnConfig"));
const fmko_ts_common_1 = require("fmko-ts-common");
const SchemaConstants_1 = tslib_1.__importDefault(require("./SchemaConstants"));
class SchemaNavigationDefaultStrategy {
    static addCellNavigationListener(args) {
        const { schema, tdElement, row, column } = args;
        const columnConfig = schema.gridConfig.getColumn(column);
        const columnLength = schema.gridConfig.getNumberOfColumns();
        const rowLength = () => schema.getRowCount();
        const isDeleteRow = columnConfig instanceof DeleteRowSchemaColumnConfig_1.default;
        tdElement.addEventListener("focus", () => {
            if (!isDeleteRow) {
                schema.setLastFocusedCell({ row, column });
                return;
            }
            schema.getCellWidget(tdElement).focus();
            fmko_ts_common_1.DOM.setElementTabbable(tdElement, false);
        });
        tdElement.addEventListener("keydown", event => {
            if (this.isCellNavigationIsBlocked(tdElement)) {
                return;
            }
            if ((0, fmko_ts_widgets_1.isTab)(event)) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            if ((0, fmko_ts_widgets_1.isArrowUp)(event)) {
                if (this.isFirstRow(row)) {
                    return;
                }
                const cell = schema.getElement(row - 1, column);
                if (!this.canCellBeFocused(cell)) {
                    return;
                }
                return cell.focus();
            }
            if ((0, fmko_ts_widgets_1.isArrowRight)(event)) {
                if (this.isLastColumn(column, columnLength)) {
                    return;
                }
                const cell = schema.getElement(row, column + 1);
                if (!this.canCellBeFocused(cell)) {
                    return;
                }
                return cell.focus();
            }
            if ((0, fmko_ts_widgets_1.isArrowDown)(event)) {
                if (this.isLastRow(row, rowLength())) {
                    return;
                }
                const cell = schema.getElement(row + 1, column);
                if (!this.canCellBeFocused(cell)) {
                    return;
                }
                return cell.focus();
            }
            if ((0, fmko_ts_widgets_1.isArrowLeft)(event)) {
                if (this.isFirstColumn(column)) {
                    return;
                }
                const cell = schema.getElement(row, column - 1);
                if (!this.canCellBeFocused(cell)) {
                    return;
                }
                return cell.focus();
            }
            if ((0, fmko_ts_widgets_1.isHome)(event)) {
                if (event.ctrlKey) {
                    return schema.getElement(0, 0).focus(); // Upper left corner
                }
                return this.getFirstFocusableCellInRow({ schema, row, columnLength })?.focus();
            }
            if ((0, fmko_ts_widgets_1.isEnd)(event)) {
                if (event.ctrlKey) {
                    return schema.getElement(rowLength() - 1, columnLength - 1).focus(); // Bottom right corner
                }
                return this.getLastFocusableCellInRow({ schema, row, columnLength })?.focus();
            }
            if ((0, fmko_ts_widgets_1.isPageUp)(event)) {
                // Might change in the future to only move X amount of rows up,
                // in case of schemas larger than the viewport.
                return this.getFirstFocusableCellInColumn({ schema, rowLength: rowLength(), column })?.focus();
            }
            if ((0, fmko_ts_widgets_1.isPageDown)(event)) {
                // Might change in the future to only move X amount of rows down,
                // in case of schemas larger than the viewport.
                return this.getLastFocusableCellInColumn({ schema, rowLength: rowLength(), column })?.focus();
            }
            if ((0, fmko_ts_widgets_1.isBackspace)(event) || (0, fmko_ts_widgets_1.isDelete)(event)) {
                return schema.getCellWidget(tdElement).clearValue(true);
            }
            if ((0, fmko_ts_widgets_1.isEnter)(event)) {
                return schema.getCellWidget(tdElement).focus();
            }
            if ((0, fmko_ts_widgets_1.isTypeableCharacterEvent)(event)) {
                schema.getCellWidget(tdElement).onCellFocusKeyDown(event);
            }
        });
    }
    static addFieldElementListener(args) {
        const { fieldElement, renderingContext } = args;
        const { currentCell } = renderingContext;
        fieldElement.addEventListener("keydown", event => {
            if ((0, fmko_ts_widgets_1.isTab)(event)) {
                return;
            }
            if ((0, fmko_ts_widgets_1.isNavigationKey)(event)) {
                event.stopPropagation();
                return;
            }
            if ((0, fmko_ts_widgets_1.isEnter)(event)) {
                // TODO: Make sure it navigates to next cell according to SchemaLayout.ts' logic
                // TODO: Placeholder function:
                event.stopPropagation();
                currentCell.focus();
                return;
            }
            if ((0, fmko_ts_widgets_1.isEscape)(event)) {
                event.stopPropagation();
                if (this.isCellNavigationIsBlocked(currentCell)) {
                    return;
                }
                currentCell.focus();
                return;
            }
            event.stopPropagation();
        });
        fieldElement.addEventListener("focus", event => {
            currentCell.dispatchEvent(new FocusEvent("focus"));
            fmko_ts_common_1.DOM.setElementTabbable(currentCell, false);
        });
        fieldElement.addEventListener("blur", event => {
            fmko_ts_common_1.DOM.setElementTabbable(currentCell, true);
        });
    }
    static isFirstRow(currentRow) {
        return currentRow === 0;
    }
    static isLastRow(currentRow, maxRow) {
        return currentRow === (maxRow - 1);
    }
    static isFirstColumn(currentColumn) {
        return currentColumn === 0;
    }
    static isLastColumn(currentColumn, maxColumn) {
        return currentColumn === (maxColumn - 1);
    }
    static canCellBeFocused(cell) {
        return fmko_ts_common_1.DOM.isVisibleInDOM(cell) && fmko_ts_common_1.DOM.isTabbable(cell);
    }
    static getFirstFocusableCellInRow(args) {
        const { schema, row, columnLength } = args;
        let currentColumn = 0;
        let currentCell = schema.getElement(row, currentColumn);
        while (currentColumn < columnLength && !this.canCellBeFocused(currentCell)) {
            currentCell = schema.getElement(row, currentColumn);
            currentColumn++;
            if (currentColumn === columnLength) {
                return null;
            }
        }
        return currentCell;
    }
    static getLastFocusableCellInRow(args) {
        const { schema, row, columnLength } = args;
        let currentColumn = columnLength - 1;
        let currentCell = schema.getElement(row, currentColumn);
        while (currentColumn > -1 && !this.canCellBeFocused(currentCell)) {
            currentCell = schema.getElement(row, currentColumn);
            currentColumn--;
            if (currentColumn === -1) {
                return null;
            }
        }
        return currentCell;
    }
    static getFirstFocusableCellInColumn(args) {
        const { schema, rowLength, column } = args;
        let currentRow = 0;
        let currentCell = schema.getElement(currentRow, column);
        while (currentRow < rowLength && !this.canCellBeFocused(currentCell)) {
            currentCell = schema.getElement(currentRow, column);
            currentRow++;
            if (currentRow === rowLength) {
                return null;
            }
        }
        return currentCell;
    }
    static getLastFocusableCellInColumn(args) {
        const { schema, rowLength, column } = args;
        let currentRow = rowLength - 1;
        let currentCell = schema.getElement(currentRow, column);
        while (currentRow > -1 && !this.canCellBeFocused(currentCell)) {
            currentCell = schema.getElement(currentRow, column);
            currentRow--;
            if (currentRow === -1) {
                return null;
            }
        }
        return currentCell;
    }
    static isCellNavigationIsBlocked(cell) {
        return cell.classList.contains(SchemaConstants_1.default.NAVIGATION_BLOCKED);
    }
}
exports.default = SchemaNavigationDefaultStrategy;
//# sourceMappingURL=SchemaNavigationDefaultStrategy.js.map